import { usePeopleReach, useServicesProfile } from '@church/data'
import { faCalendar, faKey, faPeopleGroup, type IconData } from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import {
  ProfileAvailability,
  ProfileHeader,
  ProfilePermissions,
  ProfileSchedule,
  ProfileTeams,
} from '../components/services/PersonProfile.tsx'
import { SectionPills } from '../components/SectionPills.tsx'
import {
  ErrorState,
  MOBILE_QUERY,
  ShownSection,
  Spinner,
  useMediaQuery,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { useTabParam } from '../lib/tabs.ts'

const SECTIONS = ['Teams', 'Schedule', 'Permissions'] as const
type Section = (typeof SECTIONS)[number]
const SECTION_ICONS: Record<Section, IconData> = {
  Teams: faPeopleGroup,
  Schedule: faCalendar,
  Permissions: faKey,
}

/**
 * A person's ForServices profile (docs/14): every name in ForServices opens
 * it, for whoever may see that person (anyone else gets not found). Their
 * details on top; their teams, schedule and availability, and — for whoever
 * gives access — their Permissions. Phones and tablets show one section at
 * a time behind pills; a computer shows them side by side.
 */
export const Route = createFileRoute('/_authed/_shell/services/_sections/people/$personId')({
  validateSearch: z.object({ tab: z.enum(SECTIONS).optional() }),
  component: ProfileScreen,
})

function ProfileScreen() {
  const { personId } = Route.useParams()
  const query = useServicesProfile(personId)
  const reach = usePeopleReach()
  const profile = query.data
  // People › Yannick Baptiste; someone without a People page came from a plan.
  const people = reach === 'none' ? [] : [{ label: 'People', href: '/services/people' }]
  useBreadcrumbs([...people, { label: profile?.name ?? 'Person' }])
  const mobile = useMediaQuery(MOBILE_QUERY)
  const permissions = profile?.access !== null && profile?.access !== undefined
  const sections = SECTIONS.filter((name) => name !== 'Permissions' || permissions)
  const [section, setSection] = useTabParam(sections)

  if (query.isPending) return <Spinner />
  if (!profile)
    return (
      <ErrorState
        error={query.error}
        safe="Nothing was changed."
        onRetry={() => void query.refetch()}
      />
    )

  const serving = (
    <>
      <ProfileTeams profile={profile} />
      <ProfileAvailability profile={profile} />
    </>
  )
  return (
    <div className="space-y-6">
      <ProfileHeader profile={profile} />
      {mobile && sections.length > 1 && (
        <SectionPills
          label="Profile sections"
          sections={sections}
          icons={SECTION_ICONS}
          value={section}
          onChange={setSection}
        />
      )}
      {mobile ? (
        <ShownSection value={section}>
          <div className="plan-sections space-y-6">
            {section === 'Teams' && serving}
            {section === 'Schedule' && <ProfileSchedule profile={profile} />}
            {section === 'Permissions' && <ProfilePermissions profile={profile} />}
          </div>
        </ShownSection>
      ) : (
        <div className="grid grid-cols-2 items-start gap-6">
          <div className="min-w-0 space-y-6">
            {serving}
            {permissions && <ProfileSchedule profile={profile} />}
          </div>
          <div className="min-w-0 space-y-6">
            {permissions ? (
              <ProfilePermissions profile={profile} />
            ) : (
              <ProfileSchedule profile={profile} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
